<template>
	<div>
		<!-- History Button for manual access -->
		<EntityHistoryButton
			:entity-type="entityType"
			:entity-id="entityId"
			:executive="executive"
			:changes-method-name="changesMethodName"
		>
			<template #changes-display="{ show, changes, title, entityId, entityType, close }">
				<EntityChangesWizard
					v-if="entityId"
					:entity-type="entityType"
					:entity-id="entityId"
					:show="show"
					:changes="changes"
					:title="title"
					:hide-mark-as-read="hideMarkAsRead"
					:executive="executive"
					:recent-changes-method-name="recentChangesMethodName"
					:page-visit-method-name="pageVisitMethodName"
					@close="close"
				/>
			</template>
		</EntityHistoryButton>
		
		<!-- Automatic Changes Wizard for recent changes detection -->
		<EntityChangesWizard
			:entity-type="entityType"
			:entity-id="entityId"
			:show="showAutoWizard"
			:hide-mark-as-read="hideMarkAsRead"
			:executive="executive"
			:recent-changes-method-name="recentChangesMethodName"
			:page-visit-method-name="pageVisitMethodName"
			@close="handleAutoWizardClose"
		/>
	</div>
</template>

<script>
import EntityHistoryButton from './EntityHistoryButton.vue'
import EntityChangesWizard from './EntityChangesWizard.vue'

export default {
	name: 'EntityHistory',
	components: {
		EntityHistoryButton,
		EntityChangesWizard
	},
	props: {
		/**
		 * The type of entity (from EntityType enum)
		 */
		entityType: {
			type: String,
			required: true
		},
		/**
		 * The ID of the entity
		 */
		entityId: {
			type: String,
			required: true
		},
		/**
		 * The executive instance to use for API calls
		 */
		executive: {
			type: Object,
			required: true
		},
		/**
		 * Method name to call for getting changes since a date
		 * e.g., 'getContractorChangesSince'
		 */
		changesMethodName: {
			type: String,
			required: true
		},
		/**
		 * Method name to call for getting recent changes
		 * e.g., 'getContractorRecentChanges'
		 */
		recentChangesMethodName: {
			type: String,
			required: true
		},
		/**
		 * Method name to call for logging page visits
		 * e.g., 'logContractorPageVisit'
		 */
		pageVisitMethodName: {
			type: String,
			required: true
		},
		/**
		 * Whether to hide the "Mark as Read" button in the wizard
		 */
		hideMarkAsRead: {
			type: Boolean,
			default: true
		},
		/**
		 * Whether to automatically check for recent changes on mount
		 * Set to false to disable automatic popup behavior
		 */
		autoCheckChanges: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return {
			showAutoWizard: false,
			checkingRecentChanges: false
		}
	},
	async mounted() {
		// Automatically check for recent changes when component mounts (if enabled)
		if (this.autoCheckChanges) {
			await this.checkForRecentChanges()
		}
	},
	watch: {
		// Watch for entity ID changes and re-check for recent changes
		entityId: {
			handler(newEntityId) {
				if (newEntityId && this.autoCheckChanges) {
					this.checkForRecentChanges()
				}
			}
		}
	},
	methods: {
		async checkForRecentChanges() {
			if (!this.entityId || this.checkingRecentChanges) return
			
			this.checkingRecentChanges = true
			try {
				// Use provided executive for change detection
				const result = await this.executive[this.recentChangesMethodName](this.entityId)
				
				if (result.hasUnseenChanges) {
					// Show the changes wizard after a short delay to let the page load
					setTimeout(() => {
						this.showAutoWizard = true
					}, 1000)
				}
				
				// Log the page visit to track that the user visited this page
				const currentRoute = this.$route.path
				await this.executive[this.pageVisitMethodName](this.entityId, currentRoute)
			} catch (error) {
				console.error(`Error checking for recent ${this.entityType} changes:`, error)
			} finally {
				this.checkingRecentChanges = false
			}
		},
		
		handleAutoWizardClose() {
			this.showAutoWizard = false
		}
	}
}
</script>

<style lang="scss" scoped>
/* All styles are handled by the nested components */
</style> 